import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import { addDays, differenceInMilliseconds, isAfter, isFuture, isPast, set, subDays, subHours } from 'date-fns';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import i18next from 'i18next';
import { formatLocalizedDateShort } from '@jwp/ott-common/src/utils/formatting';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';

import { EPG_TIMELINE_DAYS_AFTER, EPG_TIMELINE_DAYS_BEFORE } from '../constants';

/**
 * Returns true when the program is currently live e.g. the startTime is before now and the endTime is after now
 */
export const programIsLive = (program: EpgProgram) => {
  const startTime = new Date(program.startTime);
  const endTime = new Date(program.endTime);

  return isPast(startTime) && isFuture(endTime);
};

/**
 * Returns true when the program is upcoming
 */
export const programIsUpcoming = (program: EpgProgram) => {
  const startTime = new Date(program.startTime);

  return isFuture(startTime);
};

/**
 * Returns true when the program is finished.
 */
export const programIsVod = (program: EpgProgram) => {
  const endTime = new Date(program.endTime);

  return isPast(endTime);
};

/**
 * Returns true when the program is watchable from the beginning. This is when the startTime is within the live stream
 * archive length.
 */
export const programIsFullyWatchable = (program: EpgProgram, liveStreamCatchupHours = 8) => {
  const startTime = new Date(program.startTime);
  const maxStartTime = subHours(new Date(), liveStreamCatchupHours);

  return isAfter(startTime, maxStartTime);
};

/**
 * Get the live program of the given channel
 */
export const getLiveProgram = (channel: EpgChannel) => {
  return channel.programs.find(programIsLive);
};

// Formats a date to a localized string, optionally with relative labels for today, tomorrow, and yesterday.
export const formatEpgDate = (date: Date, useRelativeLabels = true) => {
  if (useRelativeLabels) {
    if (date.toDateString() === new Date().toDateString()) {
      return i18next.t('epg:today');
    }
    if (date.toDateString() === addDays(new Date(), 1).toDateString()) {
      return i18next.t('epg:tomorrow');
    }
    if (date.toDateString() === subDays(new Date(), 1).toDateString()) {
      return i18next.t('epg:yesterday');
    }
  }

  return formatLocalizedDateShort(date, i18next.language);
};

export const getDayOptions = (daysBefore = EPG_TIMELINE_DAYS_BEFORE, daysAfter = EPG_TIMELINE_DAYS_AFTER) => {
  const today = new Date();

  return Array.from({ length: daysBefore + daysAfter + 1 }).map((_, index) => {
    const date = addDays(today, index - daysBefore);
    const value = (index - daysBefore).toString();
    const label = formatEpgDate(date);

    return { label, value };
  });
};

export const getProgramClosestToSameTime = (programs: EpgProgram[]) => {
  const now = new Date();
  return programs
    .map((program) => ({
      program,
      diff: Math.abs(
        // Normalize each date
        differenceInMilliseconds(
          set(now, {
            hours: new Date(program.endTime).getHours(),
            minutes: new Date(program.endTime).getMinutes(),
            seconds: 0,
            milliseconds: 0,
          }),
          now,
        ),
      ),
    }))
    .sort((a, b) => a.diff - b.diff)[0]?.program;
};

export const getProgramClosestToTimestamp = (programs: EpgProgram[], timestamp = Date.now()) => {
  const exactProgram = programs.find((program) => new Date(program.startTime).getTime() <= timestamp && new Date(program.endTime).getTime() >= timestamp);
  if (exactProgram) return exactProgram;

  const sortedPrograms = programs
    .map((program) => ({ diff: Math.abs(timestamp - new Date(program.startTime).getTime()), program }))
    .sort((a, b) => a.diff - b.diff);

  return sortedPrograms[0]?.program;
};

export const generatePlaylistFromPrograms = (title: string, programs: { channel: EpgChannel; program: EpgChannel['programs'][number] }[]): Playlist => {
  const playlist: Playlist = {
    id: title,
    title,
    playlist: programs.map(({ channel, program }) => {
      const startDate = new Date(program.startTime);
      const endDate = new Date(program.endTime);
      const mediaStatus =
        startDate && endDate
          ? isPast(startDate) && isFuture(endDate)
            ? MediaStatus.LIVE
            : isPast(endDate)
            ? MediaStatus.VOD
            : MediaStatus.SCHEDULED
          : undefined;

      const playlistItem: PlaylistItem = {
        mediaid: `${channel.id}-${program.id}`,
        title: program.title,
        image: channel.backgroundImage,
        cardImage: program.cardImage || program.backgroundImage || channel.backgroundImage,
        channelLogoImage: channel.backgroundImage,
        channelTitle: channel.title,
        description: program.description || '',
        mediaStatus,
        duration: mediaStatus === MediaStatus.LIVE ? 0 : (endDate.getTime() - startDate.getTime()) / 1000,
        free: '1', // @TODO we assume all live channels are "free", but this might not be the case
        pubdate: startDate.getTime(),
        contentType: 'livechannel',
        scheduledStart: startDate,
        scheduledEnd: endDate,
        liveChannelsId: channel.id,
        feedid: channel.id,
        link: '',
        sources: [],
        images: [],
        metadata: {
          channelId: channel.id,
          programId: program.id,
          startTime: program.startTime,
          endTime: program.endTime,
        },
      };
      return playlistItem;
    }),
  };
  return playlist;
};

export const getLivePlaylistFromChannels = (title: string, channels: EpgChannel[]) => {
  const programs = channels
    .map((channel) => {
      const now = Date.now();
      const program = channel.programs.find((prog) => new Date(prog.startTime).getTime() <= now && new Date(prog.endTime).getTime() > now);
      return program ? { channel, program } : null;
    })
    .filter((item): item is { channel: EpgChannel; program: EpgChannel['programs'][number] } => item !== null);

  return generatePlaylistFromPrograms(title, programs);
};

export const getSoonToStartPlaylistFromChannels = (title: string, channels: EpgChannel[]) => {
  const programs = channels
    .map((channel) => {
      const now = Date.now();
      const program = channel.programs.find(
        (prog) => new Date(prog.startTime).getTime() > now && new Date(prog.startTime).getTime() <= now + 3 * 60 * 60 * 1000,
      );
      return program ? { channel, program } : null;
    })
    .filter((item): item is { channel: EpgChannel; program: EpgChannel['programs'][number] } => item !== null && item.program.navigable)
    .sort((a, b) => {
      const aStart = new Date(a.program.startTime).getTime();
      const bStart = new Date(b.program.startTime).getTime();
      return aStart - bStart;
    });

  return generatePlaylistFromPrograms(title, programs);
};

export const getJustFinishedPlaylistFromChannels = (title: string, channels: EpgChannel[]) => {
  const programs = channels
    .flatMap((channel) => {
      const now = Date.now();
      const matchingPrograms = channel.programs.filter(
        (prog) => new Date(prog.endTime).getTime() <= now && new Date(prog.endTime).getTime() > now - 3 * 60 * 60 * 1000,
      );
      return matchingPrograms.map((program) => ({ channel, program }));
    })
    .filter((item) => {
      return isAfter(new Date(item.program.startTime), subHours(new Date(), item.channel?.catchupHours ?? 0));
    })
    .sort((a, b) => {
      const aEnd = new Date(a.program.endTime).getTime();
      const bEnd = new Date(b.program.endTime).getTime();
      return bEnd - aEnd;
    });

  return generatePlaylistFromPrograms(title, programs);
};
